import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Film, Users, BookOpen, Clock, Sparkles, Trash2, BookMarked, TrendingUp, Activity, HardDrive, AlertTriangle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color }) => {
  return (
    <div className="p-6 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
          <Icon size={24} style={{ color }} />
        </div>
        <span className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>{value}</span>
      </div>
      <p className="text-sm font-medium" style={{ color: '#8197A4' }}>{label}</p>
    </div>
  );
};

const FeaturedOverviewMini = () => {
  const [queue, setQueue] = useState([]);
  const [settings, setSettings] = useState({ mode: 'random' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [queueRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/featured-queue`, { withCredentials: true }),
        axios.get(`${API}/admin/featured-settings`, { withCredentials: true })
      ]);
      setQueue(queueRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const isDateActive = (item) => {
    const now = new Date().toISOString();
    if (item.start_date && now < item.start_date) return false;
    if (item.end_date && now > item.end_date) return false;
    return true;
  };

  if (loading) {
    return <p className="text-center py-4" style={{ color: '#8197A4' }}>Loading...</p>;
  }

  const activeCount = queue.filter(isDateActive).length;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#0F171E' }}>
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#8197A4' }}>Mode</p>
          <p className="text-lg font-bold" style={{ color: settings.mode === 'manual' ? '#3B82F6' : '#F0C14B' }}>
            {settings.mode === 'manual' ? 'Manual Queue' : 'Random Rotation'}
          </p>
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#0F171E' }}>
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#8197A4' }}>Queued Films</p>
          <p className="text-lg font-bold" style={{ color: '#FFFFFF' }}>{queue.length}</p>
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#0F171E' }}>
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#8197A4' }}>Active Now</p>
          <p className="text-lg font-bold" style={{ color: '#10B981' }}>{activeCount}</p>
        </div>
      </div>

      {queue.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#8197A4' }}>Currently Featured</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {queue.slice(0, 6).map(item => (
              <div key={item.queue_id} className="rounded-lg overflow-hidden" style={{ backgroundColor: '#0F171E', opacity: isDateActive(item) ? 1 : 0.4 }}>
                <div style={{ aspectRatio: '2/3' }}>
                  <img src={mediaUrl(item.movie?.poster_path, API) || `https://picsum.photos/seed/${item.movie_id}/200/300`}
                    alt={item.movie?.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <p className="text-xs font-semibold truncate" style={{ color: '#FFFFFF' }}>{item.movie?.title}</p>
                  {item.movie?.overlay_label && (
                    <span className="text-xs" style={{ color: '#F0C14B' }}>• {item.movie.overlay_label}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {queue.length === 0 && (
        <p className="text-center py-6 text-sm" style={{ color: '#8197A4' }}>No featured films configured yet</p>
      )}
    </div>
  );
};

const CreateUserModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    membership_id: '',
    email: '',
    password: '',
    date_of_birth: '',
    generate_password: false,
    force_password_reset: false
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        membership_id: formData.membership_id,
        date_of_birth: formData.date_of_birth,
        email: formData.email || null,
        password: formData.generate_password ? null : formData.password,
        generate_password: formData.generate_password,
        force_password_reset: formData.force_password_reset
      };
      const { data } = await axios.post(`${API}/admin/users`, payload, { withCredentials: true });
      if (data.password) {
        setGeneratedPassword(data.password);
      } else {
        toast.success('User created successfully');
        onClose();
        onCreated();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    backgroundColor: '#0F171E',
    border: '1px solid #2A3F52',
    color: '#FFFFFF'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
      <div className="w-full max-w-md p-6 rounded-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#FFFFFF' }}>Create Member</h2>
        
        {generatedPassword ? (
          <div>
            <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid #3B82F6' }}>
              <p className="text-sm mb-2" style={{ color: '#DDDDDD' }}>User created! Generated password:</p>
              <p className="font-mono text-lg font-bold" style={{ color: '#3B82F6' }}>{generatedPassword}</p>
              <p className="text-xs mt-2" style={{ color: '#8197A4' }}>Save this password. It won&apos;t be shown again.</p>
            </div>
            <button
              onClick={() => { onClose(); onCreated(); setGeneratedPassword(''); }}
              className="w-full py-3 rounded-lg font-semibold"
              style={{ backgroundColor: '#3B82F6', color: 'white' }}
              data-testid="close-modal-button"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={inputStyle}
                required
                data-testid="new-user-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Club Membership ID *</label>
              <input
                type="text"
                value={formData.membership_id}
                onChange={(e) => setFormData({ ...formData, membership_id: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={inputStyle}
                required
                placeholder="e.g. CLB-00234"
                data-testid="new-user-membership-id"
              />
              <p className="text-xs mt-1" style={{ color: '#8197A4' }}>Unique identifier for this member</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Date of Birth *</label>
              <input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={inputStyle}
                required
                max={new Date().toISOString().split('T')[0]}
                data-testid="new-user-dob"
              />
              <p className="text-xs mt-1" style={{ color: '#8197A4' }}>Used for age-based content restrictions</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Email (optional)</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 rounded-lg outline-none"
                style={inputStyle}
                data-testid="new-user-email"
              />
              <p className="text-xs mt-1" style={{ color: '#8197A4' }}>Only needed for password recovery</p>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.generate_password}
                  onChange={(e) => setFormData({ ...formData, generate_password: e.target.checked, password: '' })}
                  className="w-4 h-4"
                  data-testid="generate-password-checkbox"
                />
                <span className="text-sm" style={{ color: '#DDDDDD' }}>Generate secure random password</span>
              </label>
            </div>
            {!formData.generate_password && (
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Password</label>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg outline-none"
                  style={inputStyle}
                  required
                  minLength="6"
                  data-testid="new-user-password"
                />
              </div>
            )}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.force_password_reset}
                  onChange={(e) => setFormData({ ...formData, force_password_reset: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm" style={{ color: '#DDDDDD' }}>Force password reset on first login</span>
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#3B82F6', color: 'white' }}
                data-testid="submit-new-user"
              >
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const EditUserModal = ({ isOpen, user, onClose, onSaved }) => {
  const [formData, setFormData] = useState({
    name: '',
    membership_id: '',
    email: '',
    date_of_birth: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        membership_id: user.membership_id || '',
        email: user.email || '',
        date_of_birth: user.date_of_birth || '',
      });
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {};
      if (formData.name && formData.name !== user.name) payload.name = formData.name;
      if (formData.membership_id && formData.membership_id !== user.membership_id) payload.membership_id = formData.membership_id;
      if (formData.email !== (user.email || '')) payload.email = formData.email || null;
      if (formData.date_of_birth && formData.date_of_birth !== user.date_of_birth) payload.date_of_birth = formData.date_of_birth;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        onClose();
        return;
      }
      await axios.put(`${API}/admin/users/${user._id}`, payload, { withCredentials: true });
      toast.success('User updated successfully');
      onClose();
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' };
  const dobToday = new Date().toISOString().split('T')[0];
  const computedAge = formData.date_of_birth
    ? Math.floor((new Date() - new Date(formData.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }} data-testid="edit-user-modal">
      <div className="w-full max-w-md p-6 rounded-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
        <h2 className="text-2xl font-bold mb-1" style={{ color: '#FFFFFF' }}>Edit Member</h2>
        <p className="text-sm mb-6" style={{ color: '#8197A4' }}>Update name, membership ID, email, or date of birth (age recomputes automatically)</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Full Name</label>
            <input type="text" value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle}
              data-testid="edit-user-name" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Membership ID</label>
            <input type="text" value={formData.membership_id}
              onChange={(e) => setFormData({ ...formData, membership_id: e.target.value.toUpperCase() })}
              className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle}
              data-testid="edit-user-membership-id" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Date of Birth</label>
            <input type="date" value={formData.date_of_birth}
              onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
              className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle}
              max={dobToday}
              data-testid="edit-user-dob" />
            <p className="text-xs mt-1" style={{ color: '#8197A4' }}>
              Current age on file: <span style={{ color: '#3B82F6' }}>{user.age ?? '—'}</span>
              {computedAge !== null && computedAge !== user.age && (
                <> → New age: <span style={{ color: '#F0C14B' }}>{computedAge}</span></>
              )}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#DDDDDD' }}>Email</label>
            <input type="email" value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 rounded-lg outline-none" style={inputStyle}
              placeholder="Optional — leave blank to remove"
              data-testid="edit-user-email" />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-lg font-semibold"
              style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
              data-testid="cancel-edit-user">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-3 rounded-lg font-semibold"
              style={{ backgroundColor: '#3B82F6', color: 'white' }}
              data-testid="save-edit-user">
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StorageAlertsCard = ({ alerts, onNavigate }) => {
  if (!alerts) return null;
  const list = alerts.alerts || [];
  const usedPct = alerts.r2?.pct || 0;
  const barColor = usedPct >= 95 ? '#EF4444' : usedPct >= 80 ? '#F0C14B' : usedPct >= 60 ? '#3B82F6' : '#10B981';

  return (
    <div className="mb-6 p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="storage-alerts-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <HardDrive size={22} color={list.length > 0 ? '#F0C14B' : '#10B981'} />
          <div>
            <h3 className="font-bold" style={{ color: '#FFFFFF' }}>
              Storage Health {list.length === 0 && <span className="text-xs ml-1" style={{ color: '#10B981' }}>All clear</span>}
            </h3>
            <div className="text-xs" style={{ color: '#8197A4' }}>
              Cloudflare R2: <span style={{ color: '#DDDDDD' }}>{alerts.r2?.used_human || '—'}</span>
              {' of '}<span style={{ color: '#DDDDDD' }}>{alerts.r2?.quota_human}</span> free tier
              {' · '}<span style={{ color: barColor, fontWeight: 600 }}>{usedPct.toFixed(1)}%</span> used
            </div>
          </div>
        </div>
        <button onClick={() => onNavigate('/admin/storage')}
          className="text-xs px-3 py-1.5 rounded-lg font-semibold"
          style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
          data-testid="alerts-open-storage">
          Open Storage Manager
        </button>
      </div>

      <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#2A3F52' }}>
        <div className="h-full transition-all" style={{
          width: `${Math.max(1, Math.min(100, usedPct))}%`,
          backgroundColor: barColor,
        }} />
      </div>

      {list.length > 0 && (
        <div className="mt-4 space-y-2">
          {list.map((a, i) => {
            const color = a.severity === 'critical' ? '#EF4444' : a.severity === 'warning' ? '#F0C14B' : '#3B82F6';
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg"
                style={{ backgroundColor: `${color}12`, border: `1px solid ${color}55` }}
                data-testid={`storage-alert-${a.kind}`}>
                <AlertTriangle size={16} color={color} className="mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm" style={{ color: '#FFFFFF' }}>{a.title}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#8197A4' }}>{a.message}</div>
                </div>
                {a.cta && (
                  <button onClick={() => onNavigate(a.cta_url || '/admin/storage')}
                    className="text-xs px-2.5 py-1 rounded font-semibold flex-shrink-0"
                    style={{ backgroundColor: color, color: '#0F171E' }}>
                    {a.cta}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


export const AdminDashboard = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [borrows, setBorrows] = useState([]);
  const [borrowFilter, setBorrowFilter] = useState('all');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'borrows') fetchBorrows();
    if (activeTab === 'analytics') fetchAnalytics();
  }, [activeTab, borrowFilter, analyticsDays]);

  const fetchBorrows = async () => {
    try {
      const params = borrowFilter !== 'all' ? `?status=${borrowFilter}` : '';
      const { data } = await axios.get(`${API}/admin/borrows${params}`, { withCredentials: true });
      setBorrows(data);
    } catch (error) {
      console.error('Error fetching borrows:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/analytics?days=${analyticsDays}`, { withCredentials: true });
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, moviesRes, alertsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/movies`, { withCredentials: true }),
        axios.get(`${API}/admin/storage/alerts`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setMovies(moviesRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  const tabButtonStyle = (isActive) => ({
    backgroundColor: isActive ? '#3B82F6' : '#1A2632',
    color: isActive ? 'white' : '#8197A4',
    border: isActive ? '1px solid #3B82F6' : '1px solid #2A3F52'
  });

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-8 md:px-12 lg:px-16 pb-12" style={{ backgroundColor: '#0F171E' }} data-testid="admin-dashboard">
      <h1 className="text-4xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Manage Users</h1>

      <div className="mb-8 flex gap-3 flex-wrap">
        <button
          onClick={() => setActiveTab('overview')}
          className="px-4 py-2 rounded-lg font-medium transition-colors duration-200"
          style={tabButtonStyle(activeTab === 'overview')}
          data-testid="tab-overview"
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className="px-4 py-2 rounded-lg font-medium transition-colors duration-200"
          style={tabButtonStyle(activeTab === 'users')}
          data-testid="tab-users"
        >
          Users
        </button>
        <button
          onClick={() => setActiveTab('borrows')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200"
          style={tabButtonStyle(activeTab === 'borrows')}
          data-testid="tab-borrows"
        >
          <BookMarked size={16} />
          Movies Borrowed
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors duration-200"
          style={tabButtonStyle(activeTab === 'analytics')}
          data-testid="tab-analytics"
        >
          <TrendingUp size={16} />
          Analytics
        </button>
      </div>

      {activeTab === 'overview' && stats && (
        <div>
          {/* Storage Alerts Widget */}
          <StorageAlertsCard alerts={alerts} onNavigate={navigate} />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard icon={Film} label="Total Movies" value={stats.total_movies} color="#3B82F6" />
            <StatCard icon={Users} label="Active Members" value={stats.total_members} color="#10B981" />
            <StatCard icon={BookOpen} label="Movies Borrowed" value={stats.active_borrows} color="#F0C14B" />
            <StatCard icon={Clock} label="Waitlist Count" value={stats.waitlist_count} color="#EF4444" />
          </div>

          {/* Featured Overview Card */}
          <div className="rounded-xl p-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Sparkles size={22} style={{ color: '#F0C14B' }} />
                <h3 className="text-xl font-bold" style={{ color: '#FFFFFF' }}>Featured Films Overview</h3>
              </div>
              <button onClick={() => navigate('/manage-movies?tab=featured')}
                className="text-sm font-semibold px-3 py-2 rounded-lg"
                style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3B82F6', border: '1px solid #3B82F6' }}
                data-testid="manage-featured-button">
                Manage Featured →
              </button>
            </div>
            <FeaturedOverviewMini />
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="rounded-xl p-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Members</h2>
            <button
              onClick={() => setShowCreateUser(true)}
              className="px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: '#3B82F6', color: 'white' }}
              data-testid="add-user-button"
            >
              + Add Member
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid #2A3F52' }}>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Membership ID</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Name</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Email</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Age</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Status</th>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Role</th>
                  <th className="text-right py-3 px-4 font-semibold" style={{ color: '#8197A4' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isMaster = user.role === 'master_admin';
                  const canModify = !isMaster || currentUser?.role === 'master_admin';
                  return (
                    <tr key={user._id} style={{ borderBottom: '1px solid #2A3F52' }} data-testid={`user-row-${user._id}`}>
                      <td className="py-3 px-4" style={{ color: '#FFFFFF' }}>{user.membership_id}</td>
                      <td className="py-3 px-4" style={{ color: '#FFFFFF' }}>{user.name}</td>
                      <td className="py-3 px-4" style={{ color: '#DDDDDD' }}>{user.email || '—'}</td>
                      <td className="py-3 px-4" style={{ color: '#DDDDDD' }}>{user.age}</td>
                      <td className="py-3 px-4">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{
                          backgroundColor: user.status === 'active' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(220, 38, 38, 0.15)',
                          color: user.status === 'active' ? '#10B981' : '#EF4444'
                        }}>{user.status}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{
                          backgroundColor: isMaster ? 'rgba(240, 193, 75, 0.15)' : user.role === 'admin' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(129, 151, 164, 0.15)',
                          color: isMaster ? '#F0C14B' : user.role === 'admin' ? '#3B82F6' : '#8197A4'
                        }}>
                          {isMaster ? 'MASTER' : user.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          {canModify && (
                            <>
                              <button
                                onClick={() => setEditingUser(user)}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                                style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3B82F6', border: '1px solid #3B82F6' }}
                                data-testid={`edit-user-${user._id}`}
                                title="Edit name, membership ID, date of birth, or email"
                              >
                                Edit
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Generate a new password for ${user.name}?`)) return;
                                  try {
                                    const { data } = await axios.post(`${API}/admin/users/${user._id}/reset-password`, { generate_password: true }, { withCredentials: true });
                                    if (data.new_password) {
                                      window.prompt(`New password for ${user.name}. Copy this now:`, data.new_password);
                                    }
                                    toast.success('Password reset');
                                  } catch (error) {
                                    const detail = error.response?.data?.detail;
                                    toast.error(typeof detail === 'string' ? detail : 'Failed to reset');
                                  }
                                }}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                                style={{ backgroundColor: 'rgba(240, 193, 75, 0.15)', color: '#F0C14B', border: '1px solid #F0C14B' }}
                                data-testid={`reset-password-${user._id}`}
                                title="Reset Password"
                              >
                                Reset PW
                              </button>
                              {String(currentUser?._id) !== String(user._id) && (
                                <button
                                  onClick={async () => {
                                    const newStatus = user.status === 'active' ? 'suspended' : 'active';
                                    try {
                                      await axios.put(`${API}/admin/users/${user._id}`, { status: newStatus }, { withCredentials: true });
                                      toast.success(`User ${newStatus}`);
                                      fetchData();
                                    } catch (error) {
                                      const detail = error.response?.data?.detail;
                                      toast.error(typeof detail === 'string' ? detail : 'Failed to update');
                                    }
                                  }}
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                                  style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}
                                  data-testid={`toggle-status-${user._id}`}
                                >
                                  {user.status === 'active' ? 'Suspend' : 'Activate'}
                                </button>
                              )}
                              {!isMaster && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete ${user.name}? This cannot be undone.`)) return;
                                    try {
                                      await axios.delete(`${API}/admin/users/${user._id}`, { withCredentials: true });
                                      toast.success('User deleted');
                                      fetchData();
                                    } catch (error) {
                                      const detail = error.response?.data?.detail;
                                      toast.error(typeof detail === 'string' ? detail : 'Failed to delete');
                                    }
                                  }}
                                  className="p-1.5 rounded-lg"
                                  style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444' }}
                                  data-testid={`delete-user-${user._id}`}
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </>
                          )}
                          {!canModify && (
                            <span className="text-xs italic" style={{ color: '#8197A4' }}>Master-only</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CreateUserModal
        isOpen={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        onCreated={fetchData}
      />

      <EditUserModal
        isOpen={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSaved={fetchData}
      />

      {activeTab === 'borrows' && (
        <div data-testid="borrows-tab-content">
          <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>
                Movies Borrowed <span className="text-sm font-normal" style={{ color: '#8197A4' }}>({borrows.length})</span>
              </h2>
              <p className="text-sm mt-1" style={{ color: '#8197A4' }}>Track every borrow across all members</p>
            </div>
            <div className="flex gap-2">
              {['all', 'active', 'returned', 'expired'].map(f => (
                <button
                  key={f}
                  onClick={() => setBorrowFilter(f)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors"
                  style={{
                    backgroundColor: borrowFilter === f ? '#1E3A8A' : '#1A2632',
                    color: borrowFilter === f ? 'white' : '#8197A4',
                    border: `1px solid ${borrowFilter === f ? '#1E3A8A' : '#2A3F52'}`
                  }}
                  data-testid={`borrow-filter-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
            {borrows.length === 0 ? (
              <div className="p-12 text-center">
                <BookMarked size={40} className="mx-auto mb-3" style={{ color: '#8197A4' }} />
                <p style={{ color: '#8197A4' }}>No borrow records found for this filter.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: '#0F171E', borderBottom: '1px solid #2A3F52' }}>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase" style={{ color: '#8197A4' }}>Movie</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase" style={{ color: '#8197A4' }}>Member</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase" style={{ color: '#8197A4' }}>Borrowed</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase" style={{ color: '#8197A4' }}>Expires</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase" style={{ color: '#8197A4' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {borrows.map(b => (
                      <tr key={b.borrow_id} style={{ borderBottom: '1px solid #2A3F52' }} data-testid={`admin-borrow-${b.borrow_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={mediaUrl(b.movie?.poster_path, API) || `https://picsum.photos/seed/${b.movie_id}/40/60`}
                              alt={b.movie?.title || '—'}
                              className="w-10 h-14 object-cover rounded"
                            />
                            <div>
                              <p className="font-semibold" style={{ color: '#FFFFFF' }}>{b.movie?.title || 'Unknown'}</p>
                              <p className="text-xs" style={{ color: '#8197A4' }}>{b.movie?.release_year || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium" style={{ color: '#FFFFFF' }}>{b.user?.name || '—'}</p>
                          <p className="text-xs font-mono" style={{ color: '#3B82F6' }}>{b.user?.membership_id || ''}</p>
                        </td>
                        <td className="px-4 py-3 text-sm" style={{ color: '#DDDDDD' }}>
                          {b.borrowed_at ? new Date(b.borrowed_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm" style={{ color: '#DDDDDD' }}>
                          {b.expires_at ? new Date(b.expires_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="px-2 py-1 rounded-full text-xs font-bold uppercase"
                            style={{
                              backgroundColor:
                                b.status === 'active' ? 'rgba(16, 185, 129, 0.15)' :
                                b.status === 'expired' ? 'rgba(220, 38, 38, 0.15)' :
                                'rgba(129, 151, 164, 0.15)',
                              color:
                                b.status === 'active' ? '#10B981' :
                                b.status === 'expired' ? '#EF4444' : '#8197A4'
                            }}
                          >
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div data-testid="analytics-tab-content">
          <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Analytics</h2>
              <p className="text-sm mt-1" style={{ color: '#8197A4' }}>Trends and library health at a glance</p>
            </div>
            <div className="flex gap-2">
              {[7, 14, 30, 60, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setAnalyticsDays(d)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: analyticsDays === d ? '#1E3A8A' : '#1A2632',
                    color: analyticsDays === d ? 'white' : '#8197A4',
                    border: `1px solid ${analyticsDays === d ? '#1E3A8A' : '#2A3F52'}`
                  }}
                  data-testid={`analytics-range-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {analytics ? (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Activity size={16} style={{ color: '#10B981' }} />
                    <p className="text-xs uppercase font-semibold" style={{ color: '#8197A4' }}>Live viewers</p>
                  </div>
                  <p className="text-3xl font-bold" style={{ color: '#FFFFFF' }} data-testid="live-viewers-count">{analytics.live_viewers}</p>
                </div>
                <div className="p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                  <p className="text-xs uppercase font-semibold mb-2" style={{ color: '#8197A4' }}>Total borrows ({analyticsDays}d)</p>
                  <p className="text-3xl font-bold" style={{ color: '#3B82F6' }}>
                    {analytics.daily.reduce((s, d) => s + d.borrows, 0)}
                  </p>
                </div>
                <div className="p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                  <p className="text-xs uppercase font-semibold mb-2" style={{ color: '#8197A4' }}>Active pre-bookings</p>
                  <p className="text-3xl font-bold" style={{ color: '#F0C14B' }}>{analytics.active_reservations}</p>
                </div>
                <div className="p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                  <p className="text-xs uppercase font-semibold mb-2" style={{ color: '#8197A4' }}>Auto-fulfilled bookings</p>
                  <p className="text-3xl font-bold" style={{ color: '#10B981' }} data-testid="fulfilled-reservations-count">{analytics.fulfilled_reservations}</p>
                </div>
              </div>

              {/* Borrow/Return trend */}
              <div className="p-5 rounded-xl mb-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: '#FFFFFF' }}>Borrows &amp; Returns Trend</h3>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <LineChart data={analytics.daily} margin={{ top: 5, right: 15, left: -15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2A3F52" />
                      <XAxis dataKey="date" tick={{ fill: '#8197A4', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fill: '#8197A4', fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' }} />
                      <Legend wrapperStyle={{ color: '#DDDDDD', fontSize: 12 }} />
                      <Line type="monotone" dataKey="borrows" stroke="#3B82F6" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="returns" stroke="#10B981" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Active users trend */}
              <div className="p-5 rounded-xl mb-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: '#FFFFFF' }}>Active Users &amp; New Signups</h3>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart data={analytics.daily} margin={{ top: 5, right: 15, left: -15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2A3F52" />
                      <XAxis dataKey="date" tick={{ fill: '#8197A4', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fill: '#8197A4', fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' }} />
                      <Legend wrapperStyle={{ color: '#DDDDDD', fontSize: 12 }} />
                      <Bar dataKey="active_users" fill="#F0C14B" name="active users" />
                      <Bar dataKey="new_members" fill="#EF4444" name="new members" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top borrowed + genre pie */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                  <h3 className="text-lg font-bold mb-4" style={{ color: '#FFFFFF' }}>Top Borrowed Movies ({analyticsDays}d)</h3>
                  {analytics.top_borrowed.length === 0 ? (
                    <p style={{ color: '#8197A4' }}>No borrow activity yet in this range.</p>
                  ) : (
                    <div className="space-y-2">
                      {analytics.top_borrowed.map((it, idx) => (
                        <div key={it.movie.movie_id} className="flex items-center gap-3 p-2 rounded-lg" style={{ backgroundColor: '#0F171E' }}>
                          <span className="w-6 text-center font-bold" style={{ color: '#F0C14B' }}>{idx + 1}</span>
                          <img src={mediaUrl(it.movie.poster_path, API) || `https://picsum.photos/seed/${it.movie.movie_id}/30/45`}
                            alt="" className="w-8 h-11 object-cover rounded" />
                          <p className="flex-1 truncate text-sm font-medium" style={{ color: '#FFFFFF' }}>{it.movie.title}</p>
                          <span className="text-sm font-bold" style={{ color: '#3B82F6' }}>{it.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                  <h3 className="text-lg font-bold mb-4" style={{ color: '#FFFFFF' }}>Library by Genre</h3>
                  <div style={{ width: '100%', height: 240 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={analytics.genre_breakdown}
                          dataKey="count"
                          nameKey="genre"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          innerRadius={40}
                        >
                          {analytics.genre_breakdown.map((entry, i) => (
                            <Cell key={entry.genre} fill={['#3B82F6', '#F0C14B', '#10B981', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4', '#EC4899', '#84CC16', '#6366F1'][i % 10]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' }} />
                        <Legend wrapperStyle={{ color: '#DDDDDD', fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Age rating breakdown */}
              <div className="p-5 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: '#FFFFFF' }}>Library by Age Rating</h3>
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={analytics.age_breakdown} margin={{ top: 5, right: 15, left: -15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2A3F52" />
                      <XAxis dataKey="age_rating" tick={{ fill: '#8197A4', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#8197A4', fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ backgroundColor: '#0F171E', border: '1px solid #2A3F52', color: '#FFFFFF' }} />
                      <Bar dataKey="count" fill="#3B82F6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <p style={{ color: '#8197A4' }}>Loading analytics...</p>
          )}
        </div>
      )}

    </div>
  );
};